import { useMemo } from 'react';
import { useGate } from 'statsig-react';

import { gql } from '@soundxyz/gql-string';
import type { OnData } from '@graphql/graphql-ws/client';
import { useSubscription } from '@graphql/graphql-ws/client';
import type { FragmentType, ReleaseUpdatesFragment } from '@graphql/reactQuery';
import { ReleaseCollectorUpdateDocument } from '@graphql/reactQuery';
import { ReleaseUpdatesFragmentDoc } from '@graphql/reactQuery';
import {
  getFragment,
  MinimalReleaseUpdatesDocument,
  ReleaseNftCommentsUpdatesDocument,
} from '@graphql/reactQuery';
import { FEATURE_GATES } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleaseUpdates on Release {
    id
    finalQuantity
    numSold
    isFirstPhaseCompleted
    totalSupply
    totalMinted

    chainId

    saleDetails {
      soundSwapCollected
      soundSwapRaisedInWei

      primaryCollected
      primaryRaisedInWei

      totalVolume
    }

    samBuyBufferBpsOverride
    samSellBufferBpsOverride
  }

  subscription MinimalReleaseUpdates($id: UUID!) {
    releaseUpdates(id: $id) {
      id

      finalQuantity
      numSold
      isFirstPhaseCompleted
      totalSupply
      totalMinted

      chainId

      soundSwapCollected
      soundSwapRaisedInWei

      samBuyBufferBpsOverride
      samSellBufferBpsOverride

      primaryCollected
      primaryRaisedInWei

      totalVolume
    }
  }

  subscription ReleaseCollectorUpdate($releaseId: UUID!) {
    releaseCollectorUpdates(releaseId: $releaseId) {
      releaseId
      collector {
        id
        nftsCount
        userId
        lowestNftSerialNumber
      }
      userPublicAddress
    }
  }

  subscription ReleaseNftCommentsUpdates($releaseId: UUID!) {
    releaseNftCommentUpdate(releaseId: $releaseId) {
      id
      serialNumber

      updatedAt

      isGoldenEgg
      ownerReleaseLowestNftSerialNumber

      songSlot
      ownerAvatarUrl
      ownerPublicAddress
      ownerWebappUri
      ownerArtistId
      ownerArtistName
      ownerArtistSoundHandle
      ownerUserId
      ownerReleaseNftCount
      ownerUsername
      commentMessage
    }
  }
`);

export function useSubscribeRelease({
  release,
}: {
  release: FragmentType<ReleaseUpdatesFragmentDoc>;
}) {
  const { id } = getFragment(ReleaseUpdatesFragmentDoc, release);

  const { value: enabledSubscriptions } = useGate(FEATURE_GATES.RELEASE_MINIMAL_SUBSCRIPTIONS);

  const subscriptionResult = useSubscription({
    query: MinimalReleaseUpdatesDocument,
    enabled: enabledSubscriptions,
    variables: {
      id,
    },
  });

  const subscriptionReleaseData = subscriptionResult.data?.data.releaseUpdates;

  const data = useMemo((): ReleaseUpdatesFragment => {
    const initialReleaseInfo = getFragment(ReleaseUpdatesFragmentDoc, release);
    if (!subscriptionReleaseData) return initialReleaseInfo;

    const info: ReleaseUpdatesFragment = {
      ...subscriptionReleaseData,
      saleDetails: {
        primaryCollected: subscriptionReleaseData.primaryCollected,
        primaryRaisedInWei: subscriptionReleaseData.primaryRaisedInWei,
        soundSwapCollected: subscriptionReleaseData.soundSwapCollected,
        soundSwapRaisedInWei: subscriptionReleaseData.soundSwapRaisedInWei,
        totalVolume: subscriptionReleaseData.totalVolume,
      },
    };

    return info;
  }, [release, subscriptionReleaseData]);

  return {
    ...subscriptionResult,
    data,
  };
}

export function useSubscribeReleaseCollector({
  releaseId,
  onData,
  enabled,
}: {
  releaseId: string;
  onData: OnData<typeof ReleaseCollectorUpdateDocument>;
  enabled: boolean;
}) {
  const { value: enabledSubscriptions } = useGate(FEATURE_GATES.RELEASE_NFTS_SUBSCRIPTIONS);

  return useSubscription({
    query: ReleaseCollectorUpdateDocument,
    enabled: enabledSubscriptions && enabled,
    variables: {
      releaseId,
    },
    onData,
  });
}

export function useSubscribeReleaseNftsComments({
  releaseId,
  onData,
}: {
  releaseId: string;
  onData: OnData<typeof ReleaseNftCommentsUpdatesDocument>;
}) {
  const { value: enabledSubscriptions } = useGate(
    FEATURE_GATES.RELEASE_NFTS_COMMENTS_SUBSCRIPTIONS,
  );
  return useSubscription({
    query: ReleaseNftCommentsUpdatesDocument,
    variables: {
      releaseId,
    },
    onData,
    enabled: enabledSubscriptions,
  });
}
